import { type AiTagId } from '@solinkcorp/api-types';
import { type Immutable } from 'immer';
import React, { useMemo } from 'react';
import { useControllerState } from 'solox';
import { IGNORE_THRESHOLD_WARNING } from '../../constants';
import { useHorizonStartMs } from '../../hooks/useHorizonStartMs';
import { useResolvedThreshold } from '../../hooks/useResolvedThreshold';
import { type SDMCountWidget } from '../../layout/types';
import { countEventsSince } from '../../ServiceDisplayEventsController';
import { type CommonWidgetProps } from '../../types';
import BigValue from '../BigValue';
import WidgetCard, { type WidgetCardProps } from './WidgetCard';

interface CountWidgetProps extends CommonWidgetProps {
  config: Immutable<SDMCountWidget>;
  tagIds: AiTagId[];
}

const CountWidget: React.FC<CountWidgetProps> = (props) => {
  const {
    config,
    tagIds,
    eventsController,
    editing,
    hasConnectedTags,
    hasMultipleAssignments,
    hasLine,
    widget,
  } = props;

  // Get the raw summary map from the store - this will trigger re-renders when events arrive
  const aiTagSummaryMap = useControllerState(
    eventsController.state,
    (state) => state.aiTagSummaryMap,
  );
  // Subscribe to rawEvents so the value recomputes when events arrive for window-based horizons.
  const rawEvents = useControllerState(eventsController.state, (state) => state.rawEvents);

  const horizonStartMs = useHorizonStartMs(config.horizon, widget);

  // This avoids stale closure issues with the selector function
  // can allow data collection from multiple tagIds assigned to one widget
  const value = useMemo(() => {
    // Realtime widgets compute value from the live "in zone now" event list stored in the summary map.
    if (config.horizon === 'realtime') {
      return tagIds.reduce(
        (total, tagId) => total + (aiTagSummaryMap.get(tagId)?.activeZoneEvents.length ?? 0),
        0,
      );
    }
    // All window-based horizons derive their value by iterating over `rawEvents`
    if (horizonStartMs == null) return 0;
    const kind = config.type === 'DRIVE_OFF' ? 'driveOff' : hasLine ? 'line' : 'zone';
    return countEventsSince(rawEvents, tagIds, horizonStartMs, kind);
  }, [tagIds, hasLine, config.type, config.horizon, horizonStartMs, aiTagSummaryMap, rawEvents]);

  const { value: threshold, isFallback } = useResolvedThreshold(widget, config.threshold, 'COUNT');
  const thresholdDebugLabel = isFallback ? `fallback: ${threshold}` : `override: ${threshold}`;

  let level: WidgetCardProps['level'] = 'normal';
  if (editing || hasMultipleAssignments || hasLine || threshold === IGNORE_THRESHOLD_WARNING) {
    level = 'normal';
  } else if (value >= threshold) {
    level = 'critical';
  } else if (value >= threshold * 0.8) {
    level = 'warning';
  }

  return (
    <WidgetCard
      {...props}
      level={level}
      title={widget.title}
      thresholdDebugLabel={thresholdDebugLabel}
      horizonStartMs={horizonStartMs}
      wiggleDep={config.type === 'DRIVE_OFF' ? value : undefined}
    >
      <BigValue scaleMultiplier={2}>
        {editing || !hasConnectedTags || hasMultipleAssignments ? '---' : value}
      </BigValue>
    </WidgetCard>
  );
};

export default React.memo(CountWidget);
